import React, { useMemo, useState } from 'react';
import { experienceData } from '../../../data/experienceData';
import { projectsData } from '../../../data/projectsData';
import { skillsData } from '../../../data/skillsData';
import { findSkillEvidence } from '../../../utils/skillEvidence';
import Reveal from '../../helper/reveal';
import SkillMap from './skill-map';
import './skills.css';

const periodOf = (job) => [job.startYear, job.endYear].filter(Boolean).join(' – ');

function Skills() {
    const [selected, setSelected] = useState(null);

    const evidence = useMemo(
        () => (selected ? findSkillEvidence(selected, experienceData, projectsData) : null),
        [selected]
    );
    const periods = useMemo(
        () => Object.fromEntries(experienceData.map((job) => [job.id, periodOf(job)])),
        []
    );
    const found = evidence && (evidence.roles.length > 0 || evidence.projects.length > 0);

    return (
        <section className='section skills' id='skills' aria-labelledby='skills-title'>
            <div className='container'>
                <Reveal className='section-head'>
                    <span className='eyebrow'>03 / Technical skills</span>
                    <h2 id='skills-title' className='section-title'>The toolkit I work with.</h2>
                    <p className='skills__hint'>Select any skill to see where I’ve used it.</p>
                </Reveal>

                <div className='skills__grid'>
                    {skillsData.map((group, index) => (
                        <Reveal key={group.category} delay={(index % 3) * 80} className='skills__group'>
                            <h3>{group.category}</h3>
                            <ul>
                                {group.items.map((item) => (
                                    <li key={item}>
                                        <button
                                            type='button'
                                            className={`skills__item${selected === item ? ' is-selected' : ''}`}
                                            aria-pressed={selected === item}
                                            onClick={() => setSelected(selected === item ? null : item)}
                                        >
                                            {item}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    ))}
                </div>

                <div className='skills__evidence' aria-live='polite'>
                    {selected && (
                        <div className='skills__panel'>
                            <h3>Where I’ve used <span>{selected}</span></h3>

                            {!found && (
                                <p className='skills__empty'>
                                    Not tied to a specific role or project listed here yet. Ask me about it.
                                </p>
                            )}

                            {found && (
                                <div className='skills__results'>
                                    {evidence.roles.length > 0 && (
                                        <div>
                                            <h4>Experience</h4>
                                            <ul>
                                                {evidence.roles.map((role) => (
                                                    <li key={role.id}>
                                                        <strong>{role.jobtitle}</strong>
                                                        <span className='skills__meta'>
                                                            {role.company}{periods[role.id] ? `, ${periods[role.id]}` : ''}
                                                        </span>
                                                        <ul>
                                                            {role.matches.map((text) => (
                                                                <li key={text}>{text}</li>
                                                            ))}
                                                        </ul>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {evidence.projects.length > 0 && (
                                        <div>
                                            <h4>Projects</h4>
                                            <ul>
                                                {evidence.projects.map((project) => (
                                                    <li key={project.id}>
                                                        <strong>{project.name}</strong>
                                                        <span className='skills__meta'>{project.tags.join(' · ')}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <Reveal>
                    <SkillMap />
                </Reveal>
            </div>
        </section>
    );
}

export default Skills;
